/* eslint-disable complexity */
/* eslint-disable no-magic-numbers */
import { useCallback, useMemo, useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';

import {
  generateOrganisation,
  getExcelFileValidation,
  getExcelUploadPresignedUrl,
  getFileScanStatus
} from '@futrli/predict/core/api/organisations';
import { toastError } from '@futrli/ui-components';
import {
  API_PATHS,
  APIClients,
  createDatasourceByIntegration,
  DATASOURCES_STATUS,
  getErrorMessage,
  getOnboardingStartUrl,
  interpolatePathParams,
  isInSyncingByStatus,
  Logger,
  PREDICT_PRODUCT_ID,
  REQUEST_METHODS,
  ROUTE_PARAMS,
  updateWorkspaces,
  useSelectCurrentUser,
  workspacesSelector,
  getUpdatedWorkspacesOrgs,
  fetchDataSourcePollData
} from '@futrli/shared-services';
import sleep from '@futrli/shared-services/api-client/utils/getters';
import { ExcelOrganisationValidationFailureResponse } from '@futrli-sdk/predict';
import { Integration } from '@futrli-sdk/app-store';
import { BillingModel, Organisation } from '@futrli-sdk/organisations';
import { StoreState as AuthStoreState } from '@futrli/auth';
import { TRACKING_EVENTS } from '@futrli/predict/core/constants';
import { OrganisationListItem, WorkspacesListingItem } from '@futrli-sdk/workspaces';
import { StoreState } from '@futrli/organisations/core/types';
import {
  activateProduct,
  createOrg,
  createProviderConnection /*syncNowOauth2Connection*/,
  fetchOrganisations,
  getCallbackUrl
} from '@futrli/organisations';
import { trackEvent } from '@futrli/organisations/core/tracker';
import { clearFinancialConfigQueryCache } from '@futrli/views/queries/financialConfig';

import { setupHoldingOrgWithNewDatasource } from '../../../../../skeleton/src/workspaces/api';
import { useMarkSfaUserSetupComplete } from '../../../../../skeleton/src/onboarding/hooks/use-mark-sfa-user-setup-complete';
import { getExcelValidationErrorMessage } from './utils';

export type StepType = 'ready' | 'upload' | 'scan' | 'validation' | 'create' | 'sync';

const DEFAULT_COUNTRY_CODE = 'GB';
const REFRESH_INTERVAL = 10000;
const SCAN_INTERVAL = 2000;

export const useUploadOrganisation = (workspaceId: string) => {
  const intl = useIntl();
  const { markSfaUserSetupComplete } = useMarkSfaUserSetupComplete();
  const currentUser = useSelectCurrentUser();

  const dispatch = useDispatch();
  const userId = useSelector((store: AuthStoreState) => {
    const { authReducers } = store;
    return authReducers.authenticatedUser.username;
  });
  const [uploadFailed, setUploadFailed] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [createdOrgId, setCreatedOrgId] = useState('');
  const [isInProgress, setIsInProgress] = useState(false);
  const [step, setStep] = useState<StepType>('ready');
  const [errorMessage, setErrorMessage] = useState('');

  const workspaces = useSelector((store: StoreState) => {
    const { normalised } = store;
    return workspacesSelector(normalised) as WorkspacesListingItem[];
  });

  const errorsDictionary = useMemo(
    () => ({
      PROFIT_AND_LOSS_SHEET_MISSING: intl.formatMessage({
        id: 'error.pl_sheet_missing',
        defaultMessage: 'Profit and Loss sheet is missing'
      }),
      BALANCE_SHEET_MISSING: intl.formatMessage({
        id: 'error.bs_sheet_missing',
        defaultMessage: 'Balance sheet is missing'
      }),
      COMPANY_NAME_TOO_LONG: intl.formatMessage({
        id: 'error.company_name_too_long',
        defaultMessage: 'The company name is too long'
      }),
      COMPANY_NAME_BLANK: intl.formatMessage({
        id: 'error.company_name_blank',
        defaultMessage: 'The company name is blank'
      }),
      TYPE_MUST_BE_ORGANISATION: intl.formatMessage({
        id: 'error.type_must_be_organisation',
        defaultMessage: 'Type must be Organisation'
      }),
      FIRST_MONTH_MUST_BE_VALID_MONTH: intl.formatMessage({
        id: 'error.first_month_must_be_valid',
        defaultMessage: 'The first month must be a valid month'
      }),
      FIRST_YEAR_INCORRECT_FORMAT: intl.formatMessage({
        id: 'error.first_year_incorrect_format',
        defaultMessage: 'The first year is in an incorrect format'
      }),
      FIRST_YEAR_NOT_IN_RANGE: intl.formatMessage({
        id: 'error.first_year_not_in_range',
        defaultMessage: 'The first year is not in the correct range'
      }),
      CURRENCY_NOT_SUPPORTED: intl.formatMessage({
        id: 'error.currency_not_supported',
        defaultMessage: 'The currency is not supported'
      }),
      VAT_SCHEME_IS_INVALID: intl.formatMessage({
        id: 'error.vat_scheme_invalid',
        defaultMessage: 'The VAT scheme is invalid'
      }),
      VAT_FREQUENCY_IS_INVALID: intl.formatMessage({
        id: 'error.vat_frequency_is_invalid',
        defaultMessage: 'The VAT frequency is invalid'
      }),
      DATE_FORMAT_INCORRECT: intl.formatMessage({
        id: 'error.date_format_incorrect',
        defaultMessage: 'The format of the date is incorrect'
      }),
      FIRST_DATE_NOT_START_OF_FINANCIAL_YEAR: intl.formatMessage({
        id: 'error.selected_month_year_not_match_financial_year_start',
        defaultMessage:
          'The month and/or year does not match the month and/or year selected as the start of the financial year. Please update cell to reflect the start of the financial year'
      }),
      DATE_ROW_HAD_BLANKS: intl.formatMessage({
        id: 'error.row_of_dates_had_blanks',
        defaultMessage: 'the row of dates has blanks'
      }),
      NOT_CONSECUTIVE_DATES: intl.formatMessage({
        id: 'error.dates_not_consecutive',
        defaultMessage: 'Dates are not consecutive'
      }),
      INVALID_CATEGORY: intl.formatMessage({
        id: 'error.invalid_category',
        defaultMessage: 'category is invalid'
      }),
      DUPLICATED_ACCOUNT_NAME: intl.formatMessage({
        id: 'error.duplicated_account_name',
        defaultMessage: 'Account name is duplicated'
      }),
      MAIN_BANK_MISSING: intl.formatMessage({
        id: 'error.main_bank_missing',
        defaultMessage: 'Main bank account is missing'
      }),
      MAIN_BANK_ACCOUNT_NAME_EMPTY: intl.formatMessage({
        id: 'error.main_bank_name_empty',
        defaultMessage: 'The name of the main bank account is empty'
      }),
      MAIN_BANK_INCORRECT_CATEGORY: intl.formatMessage({
        id: 'error.main_bank_incorrect_category',
        defaultMessage: 'Main bank account has an incorrect category'
      }),
      MAIN_BANK_MORE_THAN_ONE: intl.formatMessage({
        id: 'error.main_bank_more_than_one',
        defaultMessage: 'There is more than one main bank account'
      }),
      ACCOUNTS_RECEIVABLE_MISSING: intl.formatMessage({
        id: 'error.accounts_receivable_missing',
        defaultMessage: 'Accounts receivable is missing'
      }),
      ACCOUNTS_RECEIVABLE_ACCOUNT_NAME_EMPTY: intl.formatMessage({
        id: 'error.accounts_receivable_name_empty',
        defaultMessage: 'Accounts receivable name is empty'
      }),
      ACCOUNTS_RECEIVABLE_INCORRECT_CATEGORY: intl.formatMessage({
        id: 'error.accounts_receivable_incorrect_category',
        defaultMessage: 'Accounts receivable has an incorrect category'
      }),
      ACCOUNTS_RECEIVABLE_MORE_THAN_ONE: intl.formatMessage({
        id: 'error.accounts_receivable_more_than_one',
        defaultMessage: 'There is more than one accounts receivable'
      }),
      ACCOUNTS_PAYABLE_MISSING: intl.formatMessage({
        id: 'error.accounts_payable_missing',
        defaultMessage: 'Accounts payable is missing'
      }),
      ACCOUNTS_PAYABLE_ACCOUNT_NAME_EMPTY: intl.formatMessage({
        id: 'error.accounts_payable_name_empty',
        defaultMessage: 'The name of the accounts payable is empty'
      }),
      ACCOUNTS_PAYABLE_INCORRECT_CATEGORY: intl.formatMessage({
        id: 'error.accounts_payable_incorrect_category',
        defaultMessage: 'Accounts payable has an incorrect category'
      }),
      ACCOUNTS_PAYABLE_MORE_THAN_ONE: intl.formatMessage({
        id: 'error.accounts_payable_more_than_one',
        defaultMessage: 'There is more than one accounts payable'
      }),
      VAT_CONTROL_ACCOUNT_MISSING: intl.formatMessage({
        id: 'error.vat_control_account_missing',
        defaultMessage: 'VAT control account is missing'
      }),
      VAT_CONTROL_ACCOUNT_ACCOUNT_NAME_EMPTY: intl.formatMessage({
        id: 'error.vat_control_account_name_empty',
        defaultMessage: 'The name of the VAT control account is empty'
      }),
      VAT_CONTROL_ACCOUNT_INCORRECT_CATEGORY: intl.formatMessage({
        id: 'error.va_control_account_incorrect_category',
        defaultMessage: 'VAT control account has an incorrect category'
      }),
      VAT_CONTROL_ACCOUNT_MORE_THAN_ONE: intl.formatMessage({
        id: 'error.vat_control_account_more_than_one',
        defaultMessage: 'There is more than one VAT control account'
      }),
      RETAINED_EARNINGS_ACCOUNT_MISSING: intl.formatMessage({
        id: 'error.retained_earnings_account_missing',
        defaultMessage: 'The retained earnings account is missing'
      }),
      RETAINED_EARNINGS_ACCOUNT_ACCOUNT_NAME_EMPTY: intl.formatMessage({
        id: 'error.retained_earnings_account_name_empty',
        defaultMessage: 'The name of the retained earnings account is empty'
      }),
      RETAINED_EARNINGS_ACCOUNT_INCORRECT_CATEGORY: intl.formatMessage({
        id: 'error.retained_earnings_account_incorrect_category',
        defaultMessage: 'The retained earnings account has an incorrect category'
      }),
      RETAINED_EARNINGS_ACCOUNT_MORE_THAN_ONE: intl.formatMessage({
        id: 'error.retained_earnings_account_more_than_one',
        defaultMessage: 'There is more than one retained earnings account'
      }),
      ORGANISATION_NAME_ALREADY_IN_WORKSPACE: intl.formatMessage({
        id: 'error.organisation_name_already_in_workspace',
        defaultMessage: 'The organisation name is already in the workspace'
      }),
      DATES_DO_NOT_MATCH: intl.formatMessage({
        id: 'error.dates_do_not_match',
        defaultMessage: 'The dates do not match'
      }),
      SHEET_NOT_BALANCED: intl.formatMessage({
        id: 'error.data_for_date_not_balanced_by',
        defaultMessage: 'The data for date #date# is out of balance by #amount#'
      }),
      YEAR_END_NOT_BALANCED: intl.formatMessage({
        id: 'error.data_for_date_not_balanced_by',
        defaultMessage: 'The data for date #date# is out of balance by #amount#'
      }),
      DUPLICATED_ACCOUNT_CODE: intl.formatMessage({
        id: 'error.duplicated_account_code',
        defaultMessage: 'There is a duplicated account code'
      }),
      PROFIT_AND_LOSS_DATES_MISSING: intl.formatMessage({
        id: 'error.profit_loss_dates_missing',
        defaultMessage: 'Profit and loss dates are missing'
      }),
      INVALID_FILE: intl.formatMessage({
        id: 'error.invalid_file',
        defaultMessage: 'Invalid file'
      }),
      AMOUNT_OUT_OF_RANGE: intl.formatMessage({
        id: 'error.amount_out_of_range',
        defaultMessage: 'Amount is out of range'
      }),
      AMOUNT_NOT_NUMBER: intl.formatMessage({
        id: 'error.amount_not_number',
        defaultMessage: 'Amount is not a number'
      }),
      AMOUNT_NOT_ROUNDED: intl.formatMessage({
        id: 'error.amount_not_rounded',
        defaultMessage: 'Amount is not rounded'
      }),
      MAIN_BANK_INCORRECT_PREFIX: intl.formatMessage({
        id: 'error.main_bank_incorrect_prefix',
        defaultMessage:
          'The account set as the default main bank account is invalid. The account code must start with a 51.'
      }),
      ACCOUNTS_RECEIVABLE_INCORRECT_PREFIX: intl.formatMessage({
        id: 'error.accounts_receivable_incorrect_prefix',
        defaultMessage:
          'The account set as the default accounts receivable account is invalid. The account code must start with a 41.'
      }),
      ACCOUNTS_PAYABLE_INCORRECT_PREFIX: intl.formatMessage({
        id: 'error.accounts_payable_incorrect_prefix',
        defaultMessage:
          'The account set as the default accounts payable account is invalid. The account code must start with a 40.'
      }),
      VAT_CONTROL_ACCOUNT_INCORRECT_PREFIX: intl.formatMessage({
        id: 'error.vat_control_account_incorrect_prefix',
        defaultMessage: 'The account set as the default TVA account is invalid. The account code must start with a 44.'
      }),
      RETAINED_EARNINGS_ACCOUNT_INCORRECT_PREFIX: intl.formatMessage({
        id: 'error.retained_earnings_account_incorrect_prefix',
        defaultMessage:
          'The account set as the default retained earnings account is invalid. The account code must start with a 12.'
      }),
      ACCOUNT_NAME_BLANK: intl.formatMessage({
        id: 'error.account_name_blank',
        defaultMessage: 'No account name has been entered. All accounts must have an account code and account name'
      }),
      VERSION_FORMAT_INVALID: intl.formatMessage({
        id: 'error.version_format_invalid',
        defaultMessage:
          'The version number is invalid. Please download the latest template version, or contact our Support Team for help'
      }),
      UNKNOWN_ACCOUNT_CODE: intl.formatMessage({
        id: 'error.unknown_account_code',
        defaultMessage: 'The account code is invalid. Account codes must have at least 6 digits'
      })
    }),
    [intl]
  );
  const sheetName = useMemo(
    () => ({
      BALANCE: intl.formatMessage({
        id: 'financial_fields.type.balance_sheet',
        defaultMessage: 'Balance Sheet'
      }),
      PROFIT_AND_LOSS: intl.formatMessage({
        id: 'financial_fields.type.profit_and_loss',
        defaultMessage: 'Profit & Loss'
      }),
      SYSTEM_ACCOUNTS: intl.formatMessage({
        id: 'financial_fields.type.system_accounts',
        defaultMessage: 'System Accounts'
      })
    }),
    [intl]
  );

  const refreshOrganisations = useCallback(async () => {
    const orgs = await fetchOrganisations(workspaceId);
    const updatedWorkspaces = getUpdatedWorkspacesOrgs(workspaceId, workspaces, orgs);
    updateWorkspaces(updatedWorkspaces, dispatch);
  }, [workspaceId, workspaces, dispatch]);

  const handleError = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (e: any) => {
      Logger.error(e as string);
      let errorMessage = getErrorMessage(e);
      if (typeof errorMessage === 'string' && errorMessage.toLocaleLowerCase() === 'network error') {
        errorMessage = intl.formatMessage({
          id: 'error.failed_upload_file_open_other_app',
          defaultMessage:
            'Failed to upload the file. Please ensure the file is not open in another application, then try again.'
        });
      }
      toastError(getErrorMessage(errorMessage));
      setUploadFailed(true);
    },
    [intl]
  );

  const uploadScanValidateExcelFile = useCallback(
    async (selectedFile: File, orgId?: string, countryCode?: string) => {
      setErrorMessage('');
      setCreatedOrgId('');
      if (!selectedFile) {
        toastError(intl.formatMessage({ id: 'common.please_select_file', defaultMessage: 'Please select a file' }));
      }
      setStep('upload');
      setIsInProgress(true);
      setUploadFailed(false);
      setUploadSuccess(false);
      const res = await getExcelUploadPresignedUrl(workspaceId, selectedFile.name, 'organisation');
      if (res.excel_file_id && res.upload_url?.url) {
        const formData = new FormData();
        Object.entries(res.upload_url.fields).forEach(([field, value]) => {
          formData.append(field, value as string);
        });
        formData.append('Content-Type', selectedFile.type);
        formData.append('file', selectedFile);
        await axios.post(res.upload_url.url, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        // scan status
        setStep('scan');
        let scanStatus = { scan_status: 'pending' };
        while (scanStatus.scan_status === 'pending') {
          scanStatus = await getFileScanStatus(workspaceId, res.excel_file_id);
          await sleep(SCAN_INTERVAL);
        }
        if (scanStatus.scan_status !== 'clean') {
          setErrorMessage(
            intl.formatMessage({
              id: 'error.scan_error_description',
              defaultMessage: 'We found a problem when scanning your file. Please check the file and try again. '
            })
          );
          throw new Error(
            intl.formatMessage({
              id: 'error.scan_error',
              defaultMessage: 'File scan failed.'
            })
          );
        }
        setStep('validation');
        const validationStatus = await getExcelFileValidation(workspaceId, res.excel_file_id, {
          organisation_id: orgId,
          country_code: countryCode
        });
        if ((validationStatus as ExcelOrganisationValidationFailureResponse)?.reason_code) {
          const error = validationStatus as ExcelOrganisationValidationFailureResponse;
          let reasonMessage = errorsDictionary[error.reason_code];
          if (
            ['SHEET_NOT_BALANCED', 'YEAR_END_NOT_BALANCED'].includes(error.reason_code) &&
            error.date &&
            error.amount
          ) {
            reasonMessage = errorsDictionary[error.reason_code]
              .replace('#date#', error.date)
              .replace('#amount#', `${Math.abs(parseFloat(error.amount))}`);
          }

          // Duplicated account code error has the cell references in instance_1.cell and instance_2.cell (instead of just cell)
          const sheet = error.sheet || error.instance_1?.sheet;
          const cell = error.cell || error.instance_1?.cell;
          const errorString = getExcelValidationErrorMessage(intl, reasonMessage, sheetName, sheet, cell);

          setErrorMessage(errorString);
          throw new Error(
            intl.formatMessage({
              id: 'error.validation_error',
              defaultMessage: 'File validation failed.'
            })
          );
        }
      }
      return res.excel_file_id;
    },
    [workspaceId, errorsDictionary, intl, sheetName]
  );

  const syncAndDataChecks = useCallback(
    async (datasourceId: string, orgId: string) => {
      let isInSyncing = true;
      while (isInSyncing) {
        const fetchedPollData = await fetchDataSourcePollData(`datasource=${datasourceId}`);
        isInSyncing = isInSyncingByStatus(fetchedPollData[0].sync_status as DATASOURCES_STATUS);
        await sleep(REFRESH_INTERVAL);
      }
      let dataCheckSuccess = false;
      while (!dataCheckSuccess) {
        const url = interpolatePathParams(API_PATHS.FORECAST_DATA_CHECK, {
          [ROUTE_PARAMS.ORGANISATION_ID]: orgId,
          [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
        });
        const res = await APIClients.getAPIClient().makeRequest({
          validateStatus: () => true,
          payload: {
            request: {
              method: REQUEST_METHODS.GET,
              url
            }
          }
        });
        dataCheckSuccess = res?.data?.msg === 'Financial data available';
        if (!dataCheckSuccess) {
          await sleep(REFRESH_INTERVAL);
        }
      }
      await clearFinancialConfigQueryCache({
        workspaceId, entityType: 'organisation', entityId: orgId
      }).catch(error => Logger.error(String(error)));
    },
    [workspaceId]
  );

  const createOrganisationFromExcelFile = useCallback(
    async (selectedFile: File, integration: Integration | undefined, countryCode?: string) => {
      if (!integration) {
        Logger.error('integration is undefined');
        toastError();
      }
      try {
        const fileId = await uploadScanValidateExcelFile(selectedFile, undefined, countryCode || DEFAULT_COUNTRY_CODE);
        setStep('create');
        const ds = createDatasourceByIntegration(integration!.id, [integration!]);
        const tmpOrg = { name: 'Organisation', datasource: ds };
        const org = await createOrg(workspaceId, tmpOrg);
        await activateProduct(workspaceId, org.id, PREDICT_PRODUCT_ID, BillingModel.free);

        const orgRes = await generateOrganisation(workspaceId, fileId, org.id, org.datasource.id);

        const response = await createProviderConnection(workspaceId, org.id, {
          workspace_id: workspaceId,
          organisation_id: org.id,
          datasource_id: org.datasource.id,
          integration: integration!.slug,
          user_id: userId,
          provider: 'excel',
          success_url: getCallbackUrl(getOnboardingStartUrl())
        });

        if (response?.status === 'SUCCESS' && response?.initiation_url) {
          trackEvent(TRACKING_EVENTS.ADDED_EXCEL_ORGANISATION);
          const urlObj = new URL(response.initiation_url);
          const state = urlObj.searchParams.get('state');

          await APIClients.getAPIClient().makeRequest({
            payload: {
              request: {
                method: REQUEST_METHODS.POST,
                url: urlObj.pathname,
                data: {
                  state,
                  organisation_name: orgRes.organisation_name
                }
              }
            }
          });
        }

        setStep('sync');
        await syncAndDataChecks(org.datasource.id, org.id);
        setUploadSuccess(true);
        refreshOrganisations();
        setCreatedOrgId(org.id);
      } catch (e) {
        handleError(e);
      } finally {
        setIsInProgress(false);
      }
    },
    [
      workspaceId,
      userId,
      uploadScanValidateExcelFile,
      refreshOrganisations,
      handleError,
      syncAndDataChecks
    ]
  );

  const createSfaOrganisationFromExcelFile = useCallback(
    async (
      selectedFile: File,
      businessId: string,
      workspaceByBusinessId: WorkspacesListingItem,
      organisationByBusinessId: OrganisationListItem,
      integration?: Integration,
      countryCode?: string
    ) => {
      if (!integration) {
        Logger.error('integration is undefined');
        toastError();
        return;
      }

      try {
        const fileId = await uploadScanValidateExcelFile(selectedFile, undefined, countryCode || DEFAULT_COUNTRY_CODE);
        setStep('create');

        const orgWithDs = await setupHoldingOrgWithNewDatasource(
          workspaceByBusinessId.id,
          organisationByBusinessId.id,
          integration.id
        );

        const orgRes = await generateOrganisation(
          orgWithDs.workspace_id,
          fileId,
          orgWithDs.id,
          orgWithDs.datasource.id
        );

        const response = await createProviderConnection(orgWithDs.workspace_id, orgWithDs.id, {
          workspace_id: orgWithDs.workspace_id,
          organisation_id: orgWithDs.id,
          datasource_id: orgWithDs.datasource.id,
          integration: integration.slug,
          user_id: userId,
          provider: 'excel',
          // success_url: isOnboarding
          //   ? getCallbackUrl(getProductUrl(orgWithDs.workspace_id, orgWithDs.id, PREDICT_PRODUCT_SLUG))
          //   : getCallbackUrl(getOnboardingStartUrl())
          success_url: getCallbackUrl(getOnboardingStartUrl())
        });

        await markSfaUserSetupComplete(workspaceByBusinessId.name, businessId, currentUser.attributes.email);

        if (response?.status === 'SUCCESS' && response?.initiation_url) {
          trackEvent(TRACKING_EVENTS.ADDED_EXCEL_ORGANISATION);
          const urlObj = new URL(response.initiation_url);
          const state = urlObj.searchParams.get('state');

          await APIClients.getAPIClient().makeRequest({
            payload: {
              request: {
                method: REQUEST_METHODS.POST,
                url: urlObj.pathname,
                data: {
                  state,
                  organisation_name: orgRes.organisation_name
                }
              }
            }
          });
        }

        setStep('sync');
        await syncAndDataChecks(orgWithDs.datasource.id, orgWithDs.id);
        setUploadSuccess(true);
        refreshOrganisations();
        setCreatedOrgId(orgWithDs.id);
      } catch (e) {
        handleError(e);
      } finally {
        setIsInProgress(false);
      }
    },
    [
      uploadScanValidateExcelFile,
      userId,
      markSfaUserSetupComplete,
      currentUser.attributes.email,
      syncAndDataChecks,
      refreshOrganisations,
      handleError
    ]
  );

  const updateOrganisationFromExcelFile = useCallback(
    async (selectedFile: File, integration: Integration | undefined, org: Organisation) => {
      if (!integration) {
        Logger.error('integration is undefined');
        toastError();
      }
      if (!org) {
        Logger.error('organisation is undefined');
        toastError();
      }
      try {
        const fileId = await uploadScanValidateExcelFile(selectedFile, org.id);
        if (!fileId) {
          return;
        }

        const orgRes = await generateOrganisation(workspaceId, fileId, org.id, org.datasource.id);

        setStep('sync');

        const response = await createProviderConnection(workspaceId, org.id, {
          workspace_id: workspaceId,
          organisation_id: org.id,
          datasource_id: org.datasource.id,
          integration: integration!.slug,
          user_id: userId,
          provider: 'excel',
          success_url: getCallbackUrl(getOnboardingStartUrl())
        });

        if (response?.status === 'SUCCESS' && response?.initiation_url) {
          trackEvent(TRACKING_EVENTS.ADDED_EXCEL_ORGANISATION);
          const urlObj = new URL(response.initiation_url);
          const state = urlObj.searchParams.get('state');

          await APIClients.getAPIClient().makeRequest({
            payload: {
              request: {
                method: REQUEST_METHODS.POST,
                url: urlObj.pathname,
                data: {
                  state,
                  organisation_name: orgRes.organisation_name
                }
              }
            }
          });
        }

        await syncAndDataChecks(org.datasource.id, org.id);
        setUploadSuccess(true);
        refreshOrganisations();
      } catch (e) {
        handleError(e);
      } finally {
        setIsInProgress(false);
      }
    },
    [
      workspaceId,
      uploadScanValidateExcelFile,
      userId,
      refreshOrganisations,
      handleError,
      syncAndDataChecks
    ]
  );

  return {
    createOrganisationFromExcelFile,
    createSfaOrganisationFromExcelFile,
    updateOrganisationFromExcelFile,
    uploadFailed,
    setUploadFailed,
    uploadSuccess,
    setUploadSuccess,
    isInProgress,
    step,
    setStep,
    errorMessage,
    setErrorMessage,
    createdOrgId
  };
};
